import type { NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { useAuthStore } from '@/stores/auth.store';
import { effectiveSubdomain, isRootDomain } from '@/core/http/tenantResolver';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresCentralAuth?: boolean;
    permission?: string;
    skipTenantGuard?: boolean;
    isCentral?: boolean;
    isPublicHome?: boolean;
    title?: string;
    /** Page renders usefully from offline mirrors (see router/index.ts allowlist);
     *  everything else is blocked while offline by `offlineGuard`. */
    offlineReady?: boolean;
  }
}

/** Bootstrap hydration promise, set by main.ts before router.isReady().
 *  Guards await this so the initial navigation sees hydrated auth state. */
let hydrationPromise: Promise<void> | null = null;

export function setHydrationPromise(p: Promise<void>): void {
  hydrationPromise = p;
}

async function awaitHydration(): Promise<void> {
  if (hydrationPromise) {
    await hydrationPromise;
    // Only wait once — subsequent navigations don't need to re-hydrate.
    hydrationPromise = null;
  }
}

/** If the app can't resolve a tenant subdomain we send the user into the
 *  super-admin console (`/admin/*`) so they can pick or provision a
 *  workspace. On a tenant subdomain, `/admin/*` isn't exposed —
 *  requests get bounced back to `/dashboard`. */
export const tenantGuard: NavigationGuardWithThis<undefined> = (to) => {
  // Native app: there is no marketing homepage or super-admin console. The public
  // homepage and central routes funnel into the tenant app — the authGuard then
  // sends unauthenticated users to the login screen, where they choose their
  // tenant via the "Workspace" field (there's no subdomain in a native URL).
  if (Capacitor.isNativePlatform()) {
    if (to.meta.isPublicHome || to.name === 'home' || to.meta.isCentral) {
      return { name: 'dashboard' };
    }
    return true;
  }

  const onRoot = isRootDomain();
  const sub = effectiveSubdomain();

  // Central console only shows up when there's no active tenant — either
  // the bare root or an unresolved local dev host.
  if (to.meta.isCentral) {
    if (sub && !onRoot) return { name: 'dashboard' };
    return true;
  }

  // The marketing homepage is the ROOT-domain entry only. On a tenant
  // subdomain, `/` belongs to that tenant's app, so send them there.
  if (to.meta.isPublicHome && sub && !onRoot) {
    return { path: '/dashboard' };
  }

  // Public pages (homepage, portal, billing thank-you) are reachable from ANY
  // host — check before the root-domain redirect below, or a payer landing
  // from a Stripe redirect gets bounced into the admin login.
  if (to.meta.skipTenantGuard) return true;

  // No tenant context (root domain / unresolved host) and an unknown path →
  // the public homepage. Deliberately NOT the console login: the super-admin
  // console lives at a configurable secret path (VITE_CONSOLE_PATH) and must
  // only be reachable by navigating there directly, never leaked via a
  // redirect from a guessed URL like /admin.
  if (!sub || onRoot) {
    return { name: 'home' };
  }

  return true;
};

/** Everything under AppLayout requires an authenticated session. */
export const authGuard: NavigationGuardWithThis<undefined> = async (to) => {
  if (!to.meta.requiresAuth) return true;
  // Ensure Preferences → Pinia hydration finished before we read auth state.
  await awaitHydration();
  const auth = useAuthStore();
  if (auth.isAuthenticated) return true;
  return {
    name: 'auth.login',
    query: { redirect: fullPath(to) },
  };
};

/** Central-admin auth guard for /admin/tenants/**. */
export const centralAuthGuard: NavigationGuardWithThis<undefined> = async (to) => {
  if (!to.meta.requiresCentralAuth) return true;
  await awaitHydration();
  const { useCentralAuthStore } = await import('@/stores/central-auth.store');
  const auth = useCentralAuthStore();
  if (auth.isAuthenticated) return true;
  return {
    name: 'central.login',
    query: { redirect: fullPath(to) },
  };
};

/** Route-level permission check via the permission store. A dental-only doctor
 *  who fails a check is bounced to the dental overview, not the business
 *  dashboard (which they're not allowed to see). */
export const permissionGuard: NavigationGuardWithThis<undefined> = async (to) => {
  if (!to.meta.permission) return true;
  const { usePermissionStore } = await import('@/stores/permission.store');
  const perm = usePermissionStore();
  if (perm.can(to.meta.permission)) return true;
  return { name: perm.isDoctorOnly ? 'dental-dashboard' : 'dashboard' };
};

/** Route-name prefixes a dental-only "doctor" may reach. Everything else
 *  (including ungated pages like the generic dashboard, news, notifications)
 *  bounces to the dental overview so the account is scoped to the clinic. */
const DOCTOR_ALLOWED = [
  'dental-dashboard',
  'patients',
  'treatment-plans',
  'prescriptions',
  'dental-appointments',
  'profile',
];

/** Keep dental-only doctors inside the Dental clinic module. No effect on any
 *  other user (admins, operators, cashiers navigate freely). */
export const dentalScopeGuard: NavigationGuardWithThis<undefined> = async (to) => {
  if (!to.meta.requiresAuth) return true;
  await awaitHydration();
  const { usePermissionStore } = await import('@/stores/permission.store');
  if (!usePermissionStore().isDoctorOnly) return true;
  const name = String(to.name ?? '');
  const allowed = DOCTOR_ALLOWED.some((p) => name === p || name.startsWith(`${p}.`));
  return allowed ? true : { name: 'dental-dashboard' };
};

async function currentlyOffline(): Promise<boolean> {
  try {
    return !(await Network.getStatus()).connected;
  } catch {
    return typeof navigator !== 'undefined' ? !navigator.onLine : false;
  }
}

/** Block navigation to online-only pages while offline. Pages backed by offline
 *  mirrors are flagged `offlineReady` (see router/index.ts allowlist) and pass
 *  through; everything else shows a toast and keeps the user where they are — or
 *  lands them on a safe offline page when this is the very first navigation. */
export const offlineGuard: NavigationGuardWithThis<undefined> = async (to, from) => {
  if (!to.meta.requiresAuth || to.meta.offlineReady) return true;
  if (!(await currentlyOffline())) return true;

  const { useToast } = await import('@/shared/composables/useToast');
  const { i18n } = await import('@/app/i18n');
  useToast().warning(i18n.global.t('offline.page_unavailable'), { id: 'offline-block' });

  return from.name ? false : { name: 'dashboard' };
};

function fullPath(route: RouteLocationNormalized): string {
  return route.fullPath;
}
